import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { PermissionButton } from 'src/routes/components';
import type { BatchUserEntry, ReasonCategory, TranslateFn } from '../types';
import { REASON_OPTIONS } from '../main-page';

const I18N_PREFIX = 'module.worldCup.manualIssue';

const tMI = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);


type ParseResult =
  | { ok: true; users: BatchUserEntry[] }
  | { ok: false; lineNumber: number; reason: 'format' | 'count' };

function parseInput(text: string): ParseResult {
  const lines = text.split('\n');
  const users: BatchUserEntry[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i].trim();
    if (raw) {
      const parts = raw.split(',');
      if (parts.length < 2) {
        return { ok: false, lineNumber: i + 1, reason: 'format' };
      }
      const wallet = parts[0].trim();
      const countStr = parts.slice(1).join(',').trim();
      if (!wallet) {
        return { ok: false, lineNumber: i + 1, reason: 'format' };
      }
      const count = Number(countStr);
      if (!Number.isFinite(count) || count <= 0 || !Number.isInteger(count)) {
        return { ok: false, lineNumber: i + 1, reason: 'count' };
      }
      users.push({ walletAddress: wallet, count });
    }
  }

  return { ok: true, users };
}

type BatchIssueDialogProps = {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onSubmit: (note: string, reason: ReasonCategory, users: BatchUserEntry[]) => void;
};

export default function BatchIssueDialog({ open, loading, onClose, onSubmit }: BatchIssueDialogProps) {
  const t = useTranslate();
  const [reason, setReason] = useState<ReasonCategory>('customer_support');
  const [note, setNote] = useState('');
  const [rawInput, setRawInput] = useState('');

  useEffect(() => {
    if (open) {
      setReason('customer_support');
      setNote('');
      setRawInput('');
    }
  }, [open]);

  const parsed = useMemo(() => parseInput(rawInput), [rawInput]);
  const hasInput = rawInput.trim().length > 0;
  const isValid = parsed.ok && parsed.users.length > 0;

  const errorText = !parsed.ok
    ? tMI(t, `batch.error.${parsed.reason}`, { line: parsed.lineNumber })
    : undefined;

  const helperText =
    errorText ||
    (isValid
      ? tMI(t, 'batch.parsedCount', { smart_count: parsed.users.length })
      : tMI(t, 'batch.inputHint'));

  const handleSubmit = () => {
    if (!parsed.ok || parsed.users.length === 0) return;
    onSubmit(note.trim(), reason, parsed.users);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{tMI(t, 'batch.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            size="small"
            fullWidth
            label={tMI(t, 'form.reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value as ReasonCategory)}
          >
            {REASON_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {tMI(t, option.labelKey)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label={tMI(t, 'form.memo')}
            placeholder={tMI(t, 'form.memoPlaceholder')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 500 }}
          />

          <TextField
            fullWidth
            multiline
            minRows={8}
            label={tMI(t, 'batch.usersHeader')}
            placeholder={'0x....abcd1, 10\n0x....abcd2, 20\n0x....abcd3, 30'}
            value={rawInput}
            error={hasInput && !parsed.ok}
            helperText={helperText}
            onChange={(event) => setRawInput(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('ra.action.cancel')}
        </Button>
        <PermissionButton resource="world-cup-voucher-manual-issue" action="write">
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={!isValid}
            onClick={handleSubmit}
          >
            {tMI(t, 'batch.submit')}
          </LoadingButton>
        </PermissionButton>
      </DialogActions>
    </Dialog>
  );
}
